import { GenericAbortSignal } from 'axios';

import SESSION_TOKEN_SERVICE from '@/constants/SessionTokenService';

import APIinstance from '../config';

import {
	ApplicationDto,
	ApplicationSubmissionResultDto,
	CloudFileDto,
	MetaLeadRequestDto,
	PatchRequestDto,
} from '@/generated-types';

/**
 * The draft's own credential. It carries no lifetime: the server stops honouring it when the
 * application is submitted and stops being a draft, at which point the same value addresses the
 * application on the public document-upload endpoints instead (LBF-2668).
 */
type TDraftApplicationSession = {
	application: ApplicationDto;
	accessToken: string;
};

export const initApplication = async (
	input: ApplicationDto,
	recaptchaToken: string,
	partnerSlug: string,
): Promise<ApplicationDto> => {
	const normalizedPartnerSlug = partnerSlug?.trim();
	if (!normalizedPartnerSlug) {
		throw new Error('Partner slug is required to initialize an application');
	}

	const { data } = await APIinstance.post<TDraftApplicationSession>(
		`partners/${encodeURIComponent(normalizedPartnerSlug)}/draft-applications`,
		input,
		{
			headers: { 'X-Recaptcha-Token': recaptchaToken },
		},
	);
	SESSION_TOKEN_SERVICE.setLocalAccessToken(data.accessToken);
	return data.application;
};

export const submitApplication = async (
	applicationId: string,
): Promise<ApplicationSubmissionResultDto> => {
	const { data } = await APIinstance.post<ApplicationSubmissionResultDto>(
		`applications/${applicationId}/submit`,
	);
	return data;
};

export const preAuthenticateApplication = async (
	applicationId: string,
): Promise<ApplicationSubmissionResultDto> => {
	const { data } = await APIinstance.post<ApplicationSubmissionResultDto>(
		`applications/${applicationId}/pre-authenticate`,
	);
	return data;
};

export const submitAttachments = async (
	applicationId: string,
	attachments: CloudFileDto[],
): Promise<void> => {
	await APIinstance.post<void>(`applications/${applicationId}/submit-attachments`, attachments);
};

export const getApplication = async (
	applicationId: string,
	signal?: GenericAbortSignal,
): Promise<ApplicationDto> => {
	const { data } = await APIinstance.get<ApplicationDto>(`applications/${applicationId}`, {
		signal,
	});
	return data;
};

export const patchApplication = async <T>(
	applicationId: string,
	input: (Omit<PatchRequestDto, 'op' | 'value'> & {
		value: object | T;
		op?: PatchRequestDto['op'];
	})[],
	signal?: GenericAbortSignal,
): Promise<ApplicationDto> => {
	const inputArr = [];

	for (const { value, path, op } of input) {
		inputArr.push({
			path,
			value,
			op: op ?? 'replace',
		});
	}

	const { data } = await APIinstance.patch<ApplicationDto>(
		`applications/${applicationId}`,
		JSON.stringify(inputArr),
		{
			signal,
		},
	);

	return data;
};

export const createMetaLeadApplication = async (
	payload: MetaLeadRequestDto,
): Promise<ApplicationDto> => {
	const { data } = await APIinstance.post<ApplicationDto>('leads', payload);
	return data;
};
